// src/toSlug.ts
function toSlug(str) {
  let s = str;
  if (!s) {
    return "";
  }
  s = s.toLowerCase().trim();
  s = s.replace(/ & /g, " and ");
  s = s.replace(/[ ]+/g, "-");
  s = s.replace(/[-]+/g, "-");
  s = s.replace(/[^a-z0-9-]+/g, "");
  return s;
}

// src/useIsomorphicLayoutEffect.tsx
import { useEffect, useLayoutEffect } from "react";
var useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// src/usePrevious.tsx
import * as React from "react";
function usePrevious(value) {
  const ref = React.useRef(value);
  React.useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

// src/useGenericContext.tsx
import { createContext, useContext } from "react";
var createGenericContext = (defaultContext) => {
  const genericContext = createContext(defaultContext);
  const useGenericContext = () => {
    const contextIsDefined = useContext(genericContext);
    if (!contextIsDefined) {
      throw new Error("useGenericContext must be used within a Provider");
    }
    return contextIsDefined;
  };
  return [useGenericContext, genericContext.Provider];
};

// src/useObjectState.ts
import { useState } from "react";
var useObjectState = (initialState) => {
  const [state, _setState] = useState(initialState);
  const setState = (newState) => {
    _setState((prev) => ({ ...prev, ...newState }));
  };
  return [state, setState];
};

// src/usePotentialWinnings.ts
function getPotentialWinnings(odds, decimalOdds2, stake, currency, language) {
  if (!odds || odds.length !== 2 || stake <= 0 || odds[1] === 0) {
    console.error("Invalid odds or stake values", { odds, stake });
    return formatCurrency(currency, 0, false, language, decimalOdds2);
  }
  const decimalOddsValue = odds[0] / odds[1] + 1;
  let winnings = stake * decimalOddsValue;
  if (decimalOdds2) {
    winnings = Math.round(winnings * 100) / 100;
  } else {
    winnings = Math.round(winnings);
  }
  return formatCurrency(currency, winnings, false, language, decimalOdds2);
}
function formatCurrency(code, number, forceUseCurrencySymbol, language, showDecimalPlaces) {
  let formattedParts;
  const decimalOptions = showDecimalPlaces ? {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    notation: "standard"
  } : {
    minimumFractionDigits: 0,
    notation: "compact",
    compactDisplay: "short",
    maximumSignificantDigits: 3
  };
  try {
    formattedParts = new Intl.NumberFormat(language, {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
      ...decimalOptions
    }).formatToParts(number);
  } catch (err) {
    console.error("Error formatting currency", { code, number, language, err });
    const fallbackOptions = showDecimalPlaces ? {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    } : {
      minimumFractionDigits: 0,
      maximumSignificantDigits: 3
    };
    formattedParts = new Intl.NumberFormat(language, {
      style: "currency",
      currency: code,
      currencyDisplay: "symbol",
      ...fallbackOptions
    }).formatToParts(number);
  }
  const formatted = formattedParts.map((formattedPart) => {
    const { type, value } = formattedPart;
    const shouldIgnoreCurrency = type === "currency" && value.length >= 3 && !forceUseCurrencySymbol;
    if (shouldIgnoreCurrency) {
      return "";
    }
    return value;
  }).join("").trim();
  return formatted;
}

// src/formatOdds.ts
function formatOdds(odds, format, stake, currency, language, oddsAmerican, calculateDecimalPlaces) {
  switch (format) {
    case "decimalOdds": {
      return decimalOdds(odds);
    }
    case "europeanDecimalOdds": {
      return europeanDecimalOdds(odds);
    }
    case "americanOdds": {
      return americanOdds(odds, oddsAmerican);
    }
    case "potentialWinnings": {
      return potentialWinnings(odds, currency, stake, language, calculateDecimalPlaces);
    }
    case "impliedProbability": {
      return impliedProbability(odds);
    }
    default: {
      return fractionalOdds(odds);
    }
  }
}
function decimalOdds(odds) {
  if (!isValidOdds(odds)) {
    return fallbackDisplayOdds(odds);
  }
  const decimal = odds[0] / odds[1] + 1;
  return String(decimal.toFixed(2));
}
function europeanDecimalOdds(odds) {
  if (!isValidOdds(odds)) {
    return fallbackDisplayOdds(odds);
  }
  const decimal = odds[0] / odds[1] + 1;
  return decimal.toFixed(2).replace(".", ",");
}
function americanOdds(odds, oddsAmerican) {
  if (oddsAmerican && oddsAmerican.length > 0) {
    return oddsAmerican;
  } else if (!isValidOdds(odds)) {
    return fallbackDisplayOdds(odds);
  } else if (odds[0] > odds[1]) {
    const over = odds[0] / odds[1] * 100;
    return `+${String(Math.round(over))}`;
  } else if (odds[0] < odds[1]) {
    const under = -100 / (odds[0] / odds[1]);
    return String(Math.round(under));
  } else {
    return "+100";
  }
}
function potentialWinnings(odds, currency, stake, language, calculateDecimalPlaces) {
  if (!isValidOdds(odds)) {
    return fallbackDisplayOdds(odds);
  }
  stake = stake || 10;
  currency = currency || "GBP";
  language = language || "en-GB";
  calculateDecimalPlaces = calculateDecimalPlaces ?? false;
  return getPotentialWinnings(odds, calculateDecimalPlaces, stake, currency, language);
}
function impliedProbability(odds) {
  if (!isValidOdds(odds)) {
    return fallbackDisplayOdds(odds);
  }
  const percentage = 1 / (odds[0] / odds[1] + 1) * 100;
  return `${Number(percentage.toFixed(1))}%`;
}
function fractionalOdds(odds) {
  if (!isValidOdds(odds)) {
    return fallbackDisplayOdds(odds);
  }
  return odds[0] + "/" + odds[1];
}
function isValidOdds(odds) {
  return odds[0] > 0 && odds[1] > 0;
}
function fallbackDisplayOdds(odds) {
  if (odds[0] === 0 && odds[1] === 0) {
    return "-";
  } else if (odds[0] < 0 || odds[1] < 0) {
    return "SP";
  }
  return "";
}

// src/useEffectOnce.tsx
import { useEffect as useEffect3 } from "react";
function useEffectOnce(effect) {
  useEffect3(effect, []);
}

// src/useInterval.tsx
import { useRef as useRef2, useEffect as useEffect4 } from "react";
function useInterval(callback, delay) {
  const savedCallback = useRef2(callback);
  useIsomorphicLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect4(() => {
    if (!delay && delay !== 0) {
      return;
    }
    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

// src/indexObjArray.ts
function indexObjArray(arr) {
  return arr.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
}

// src/uniqArray.ts
function uniqArray(value, index, arr) {
  return arr.indexOf(value) === index;
}

// src/translations.ts
function getLocalizedText(key, locale) {
  const translations = {
    en: {
      at: "at",
      today: "Today",
      tonight: "tonight",
      tomorrow: "tomorrow",
      in: "in",
      day: "Day",
      days: "Days",
      dayShort: "D",
      hour: "Hour",
      hours: "Hours",
      hourShort: "H",
      minute: "Minute",
      minutes: "Minutes",
      minuteShort: "M",
      second: "Second",
      seconds: "Seconds",
      secondShort: "S",
      live: "Live",
      startingSoon: "Starting Soon"
    },
    pt: {
      at: "\xE0s",
      today: "Hoje",
      tonight: "hoje \xE0 noite",
      tomorrow: "amanh\xE3",
      in: "em",
      day: "Dia",
      days: "Dias",
      dayShort: "D",
      hour: "Hora",
      hours: "Horas",
      hourShort: "H",
      minute: "Minuto",
      minutes: "Minutos",
      minuteShort: "M",
      second: "Segundo",
      seconds: "Segundos",
      secondShort: "S",
      live: "Ao vivo",
      startingSoon: "Brevemente"
    },
    // Brazilian Portuguese — only overrides the key that differs from pt
    "pt-br": {
      startingSoon: "Em breve"
    },
    es: {
      at: "a las",
      today: "Hoy",
      tonight: "esta noche",
      tomorrow: "ma\xF1ana",
      in: "en",
      day: "D\xEDa",
      days: "D\xEDas",
      dayShort: "D",
      hour: "Hora",
      hours: "Horas",
      hourShort: "H",
      minute: "Minuto",
      minutes: "Minutos",
      minuteShort: "M",
      second: "Segundo",
      seconds: "Segundos",
      secondShort: "S",
      live: "En vivo",
      startingSoon: "Pr\xF3ximamente"
    },
    de: {
      at: "um",
      today: "Heute",
      tonight: "heute Abend",
      tomorrow: "morgen",
      in: "in",
      day: "Tag",
      days: "Tage",
      dayShort: "T",
      hour: "Stunde",
      hours: "Stunden",
      hourShort: "S",
      minute: "Minute",
      minutes: "Minuten",
      minuteShort: "M",
      second: "Sekunde",
      seconds: "Sekunden",
      secondShort: "S",
      live: "Live",
      startingSoon: "Startet bald"
    },
    fr: {
      at: "\xE0",
      today: "Aujourd'hui",
      tonight: "ce soir",
      tomorrow: "demain",
      in: "dans",
      day: "Jour",
      days: "Jours",
      dayShort: "J",
      hour: "Heure",
      hours: "Heures",
      hourShort: "H",
      minute: "Minute",
      minutes: "Minutes",
      minuteShort: "M",
      second: "Seconde",
      seconds: "Secondes",
      secondShort: "S",
      live: "En direct",
      startingSoon: "Bient\xF4t"
    },
    it: {
      at: "alle",
      today: "Oggi",
      tonight: "stasera",
      tomorrow: "domani",
      in: "tra",
      day: "Giorno",
      days: "Giorni",
      dayShort: "G",
      hour: "Ora",
      hours: "Ore",
      hourShort: "O",
      minute: "Minuto",
      minutes: "Minuti",
      minuteShort: "M",
      second: "Secondo",
      seconds: "Secondi",
      secondShort: "S",
      live: "In diretta",
      startingSoon: "Inizia presto"
    },
    el: {
      today: "\u03A3\u03AE\u03BC\u03B5\u03C1\u03B1",
      day: "\u0397\u03BC\u03AD\u03C1\u03B1",
      days: "\u0397\u03BC\u03AD\u03C1\u03B5\u03C2",
      dayShort: "\u0397",
      hour: "\u038F\u03C1\u03B1",
      hours: "\u038F\u03C1\u03B5\u03C2",
      hourShort: "\u03A9",
      minute: "\u039B\u03B5\u03C0\u03C4\u03CC",
      minutes: "\u039B\u03B5\u03C0\u03C4\u03AC",
      minuteShort: "\u039B",
      second: "\u0394\u03B5\u03C5\u03C4\u03B5\u03C1\u03CC\u03BB\u03B5\u03C0\u03C4\u03BF",
      seconds: "\u0394\u03B5\u03C5\u03C4\u03B5\u03C1\u03CC\u03BB\u03B5\u03C0\u03C4\u03B1",
      secondShort: "\u0394",
      live: "\u0396\u03C9\u03BD\u03C4\u03B1\u03BD\u03AC",
      startingSoon: "\u039E\u03B5\u03BA\u03B9\u03BD\u03AC \u03C3\u03CD\u03BD\u03C4\u03BF\u03BC\u03B1"
    },
    da: {
      today: "I dag",
      day: "Dag",
      days: "Dage",
      dayShort: "D",
      hour: "Time",
      hours: "Timer",
      hourShort: "T",
      minute: "Minut",
      minutes: "Minutter",
      minuteShort: "M",
      second: "Sekund",
      seconds: "Sekunder",
      secondShort: "S",
      live: "Live",
      startingSoon: "Starter snart"
    },
    sv: {
      today: "Idag",
      day: "Dag",
      days: "Dagar",
      dayShort: "D",
      hour: "Timme",
      hours: "Timmar",
      hourShort: "T",
      minute: "Minut",
      minutes: "Minuter",
      minuteShort: "M",
      second: "Sekund",
      seconds: "Sekunder",
      secondShort: "S",
      live: "Live",
      startingSoon: "Startar snart"
    },
    fi: {
      today: "T\xE4n\xE4\xE4n",
      day: "P\xE4iv\xE4",
      days: "P\xE4iv\xE4t",
      dayShort: "P",
      hour: "Tunti",
      hours: "Tunnit",
      hourShort: "T",
      minute: "Minuutti",
      minutes: "Minuutit",
      minuteShort: "M",
      second: "Sekunti",
      seconds: "Sekunnit",
      secondShort: "S",
      live: "Live",
      startingSoon: "Alkaa pian"
    },
    sw: {
      today: "Leo",
      day: "Siku",
      days: "Siku",
      dayShort: "S",
      hour: "Saa",
      hours: "Saa",
      hourShort: "S",
      minute: "Dakika",
      minutes: "Dakika",
      minuteShort: "D",
      second: "Sekunde",
      seconds: "Sekunde",
      secondShort: "S",
      live: "Mubashara",
      startingSoon: "Inaanza hivi karibuni"
    },
    bg: {
      today: "\u0414\u043D\u0435\u0441",
      day: "\u0414\u0435\u043D",
      days: "\u0414\u043D\u0438",
      dayShort: "\u0414",
      hour: "\u0427\u0430\u0441",
      hours: "\u0427\u0430\u0441\u0430",
      hourShort: "\u0427",
      minute: "\u041C\u0438\u043D\u0443\u0442\u0430",
      minutes: "\u041C\u0438\u043D\u0443\u0442\u0438",
      minuteShort: "\u041C",
      second: "\u0421\u0435\u043A\u0443\u043D\u0434\u0430",
      seconds: "\u0421\u0435\u043A\u0443\u043D\u0434\u0438",
      secondShort: "\u0421",
      live: "\u041D\u0430 \u0436\u0438\u0432\u043E",
      startingSoon: "\u0417\u0430\u043F\u043E\u0447\u0432\u0430 \u0441\u043A\u043E\u0440\u043E"
    },
    et: {
      today: "T\xE4na",
      day: "P\xE4ev",
      days: "P\xE4evad",
      dayShort: "P",
      hour: "Tund",
      hours: "Tunnid",
      hourShort: "T",
      minute: "Minut",
      minutes: "Minutid",
      minuteShort: "M",
      second: "Sekund",
      seconds: "Sekundid",
      secondShort: "S",
      live: "Otse",
      startingSoon: "Algab peagi"
    },
    nl: {
      today: "Vandaag",
      day: "Dag",
      days: "Dagen",
      dayShort: "D",
      hour: "Uur",
      hours: "Uren",
      hourShort: "U",
      minute: "Minuut",
      minutes: "Minuten",
      minuteShort: "M",
      second: "Seconde",
      seconds: "Seconden",
      secondShort: "S",
      live: "Live",
      startingSoon: "Begint binnenkort"
    },
    ru: {
      today: "\u0421\u0435\u0433\u043E\u0434\u043D\u044F",
      day: "\u0414\u0435\u043D\u044C",
      days: "\u0414\u043D\u0438",
      dayShort: "\u0414",
      hour: "\u0427\u0430\u0441",
      hours: "\u0427\u0430\u0441\u044B",
      hourShort: "\u0427",
      minute: "\u041C\u0438\u043D\u0443\u0442\u0430",
      minutes: "\u041C\u0438\u043D\u0443\u0442\u044B",
      minuteShort: "\u041C",
      second: "\u0421\u0435\u043A\u0443\u043D\u0434\u0430",
      seconds: "\u0421\u0435\u043A\u0443\u043D\u0434\u044B",
      secondShort: "\u0421",
      live: "\u0412 \u044D\u0444\u0438\u0440\u0435",
      startingSoon: "\u0421\u043A\u043E\u0440\u043E \u043D\u0430\u0447\u043D\u0451\u0442\u0441\u044F"
    },
    ro: {
      today: "Ast\u0103zi",
      day: "Zi",
      days: "Zile",
      dayShort: "Z",
      hour: "Or\u0103",
      hours: "Ore",
      hourShort: "O",
      minute: "Minut",
      minutes: "Minute",
      minuteShort: "M",
      second: "Secund\u0103",
      seconds: "Secunde",
      secondShort: "S",
      live: "Live",
      startingSoon: "\xCEncepe \xEEn cur\xE2nd"
    },
    cs: {
      today: "Dnes",
      day: "Den",
      days: "Dny",
      dayShort: "D",
      hour: "Hodina",
      hours: "Hodiny",
      hourShort: "H",
      minute: "Minuta",
      minutes: "Minuty",
      minuteShort: "M",
      second: "Sekunda",
      seconds: "Sekundy",
      secondShort: "S",
      live: "\u017Div\u011B",
      startingSoon: "Za\u010D\xEDn\xE1 brzy"
    },
    sk: {
      today: "Dnes",
      day: "De\u0148",
      days: "Dni",
      dayShort: "D",
      hour: "Hodina",
      hours: "Hodiny",
      hourShort: "H",
      minute: "Min\xFAta",
      minutes: "Min\xFAty",
      minuteShort: "M",
      second: "Sekunda",
      seconds: "Sekundy",
      secondShort: "S",
      live: "Na\u017Eivo",
      startingSoon: "Za\u010D\xEDna \u010Doskoro"
    },
    pl: {
      today: "Dzisiaj",
      day: "Dzie\u0144",
      days: "Dni",
      dayShort: "D",
      hour: "Godzina",
      hours: "Godziny",
      hourShort: "G",
      minute: "Minuta",
      minutes: "Minuty",
      minuteShort: "M",
      second: "Sekunda",
      seconds: "Sekundy",
      secondShort: "S",
      live: "Na \u017Cywo",
      startingSoon: "Wkr\xF3tce si\u0119 zacznie"
    },
    ja: {
      today: "\u4ECA\u65E5",
      day: "\u65E5",
      days: "\u65E5",
      dayShort: "\u65E5",
      hour: "\u6642\u9593",
      hours: "\u6642\u9593",
      hourShort: "\u6642",
      minute: "\u5206",
      minutes: "\u5206",
      minuteShort: "\u5206",
      second: "\u79D2",
      seconds: "\u79D2",
      secondShort: "\u79D2",
      live: "\u30E9\u30A4\u30D6",
      startingSoon: "\u307E\u3082\u306A\u304F\u958B\u59CB"
    },
    lt: {
      today: "\u0160iandien",
      day: "Diena",
      days: "Dienos",
      dayShort: "D",
      hour: "Valanda",
      hours: "Valandos",
      hourShort: "V",
      minute: "Minut\u0117",
      minutes: "Minut\u0117s",
      minuteShort: "M",
      second: "Sekund\u0117",
      seconds: "Sekund\u0117s",
      secondShort: "S",
      live: "Tiesiogiai",
      startingSoon: "Netrukus prasid\u0117s"
    },
    lv: {
      today: "\u0160odien",
      day: "Diena",
      days: "Dienas",
      dayShort: "D",
      hour: "Stunda",
      hours: "Stundas",
      hourShort: "S",
      minute: "Min\u016Bte",
      minutes: "Min\u016Btes",
      minuteShort: "M",
      second: "Sekunde",
      seconds: "Sekundes",
      secondShort: "S",
      live: "Tie\u0161raid\u0113",
      startingSoon: "Dr\u012Bzum\u0101 s\u0101ksies"
    },
    hr: {
      today: "Danas",
      day: "Dan",
      days: "Dani",
      dayShort: "D",
      hour: "Sat",
      hours: "Sati",
      hourShort: "S",
      minute: "Minuta",
      minutes: "Minute",
      minuteShort: "M",
      second: "Sekunda",
      seconds: "Sekunde",
      secondShort: "S",
      live: "U\u017Eivo",
      startingSoon: "Po\u010Dinje uskoro"
    },
    sr: {
      today: "Danas",
      day: "Dan",
      days: "Dani",
      dayShort: "D",
      hour: "Sat",
      hours: "Sati",
      hourShort: "S",
      minute: "Minut",
      minutes: "Minuti",
      minuteShort: "M",
      second: "Sekund",
      seconds: "Sekundi",
      secondShort: "S",
      live: "U\u017Eivo",
      startingSoon: "Po\u010Dinje uskoro"
    }
  };
  const fullLocale = locale.toLowerCase().replace("_", "-");
  const baseLocale = fullLocale.split("-")[0];
  return translations[fullLocale]?.[key] ?? translations[baseLocale]?.[key] ?? translations["en"][key];
}

// src/formatDateTime.ts
var TimeFormat = /* @__PURE__ */ ((TimeFormat2) => {
  TimeFormat2["LONG"] = "long";
  TimeFormat2["SHORT"] = "short";
  return TimeFormat2;
})(TimeFormat || {});
function fmtReference(date, locale = "en-GB", timeZone = "Etc/UTC", type, option) {
  switch (type) {
    case "DAY":
      return fmtDay(date, locale, timeZone, option.dayFormat || "long" /* LONG */);
    case "MONTH":
      return fmtMonth(
        date,
        locale,
        timeZone,
        option.monthFormat || "long" /* LONG */
      );
    case "TIME":
      return fmtTime(
        date,
        locale,
        timeZone,
        option.timeFormat === "hour12",
        option.showTimezone
      );
    case "COUNTDOWN":
    case "PRE_COUNTDOWN":
      return fmtCountdownV2(
        date,
        /* @__PURE__ */ new Date(),
        option.countdownFormatStyle ?? "digital",
        option.countdownTimeGranularity ?? "H_M_S",
        timeZone,
        locale
      );
    case "DATE":
    default:
      return fmtDate(date, locale, timeZone);
  }
}
function fmtTextDate(date, locale = "en-GB", timezone = "Etc/UTC", hour12 = false) {
  const now = /* @__PURE__ */ new Date();
  const eventDate = new Date(date.toDateString());
  const todayDate = new Date(now.toDateString());
  const diffMs = eventDate.getTime() - todayDate.getTime();
  const diffDays = Math.floor(diffMs / (1e3 * 60 * 60 * 24));
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12,
    timeZone: timezone
  });
  const time = timeFormatter.format(date);
  const t = (key) => getLocalizedText(key, locale);
  if (diffDays >= 8) {
    return `${time} ${t("in")} ${diffDays} ${t("days")}`;
  }
  if (diffDays >= 2) {
    return `${time} ${fmtDay(date, locale, timezone)}`;
  }
  if (diffDays === 1) {
    return `${time} ${t("tomorrow")}`;
  }
  if (diffDays === 0) {
    const eventHour = parseInt(
      timeFormatter.formatToParts(date).find((part) => part.type === "hour")?.value || "0"
    );
    if (eventHour < 16) {
      return `${time} ${t("today")}`;
    } else {
      return `${time} ${t("tonight")}`;
    }
  }
  return timeFormatter.format(date);
}
function fmtDate(date, locale, timeZone = "Etc/UTC") {
  const day = date.toLocaleDateString(locale, {
    day: "numeric",
    timeZone
  });
  return `${day}`;
}
function fmtDay(date, locale, timeZone = "Etc/UTC", weekday = "long" /* LONG */) {
  const day = date.toLocaleDateString(locale, {
    weekday,
    timeZone
  });
  return day.at(0)?.toUpperCase() + day.slice(1);
}
function getCalendarDaysDiff(currentDate, targetDate, timezone) {
  const getDateInTimezone = (date) => {
    return date.toLocaleDateString("en-CA", { timeZone: timezone });
  };
  const currentDateStr = getDateInTimezone(currentDate);
  const targetDateStr = getDateInTimezone(targetDate);
  const currentDay = /* @__PURE__ */ new Date(currentDateStr + "T00:00:00Z");
  const targetDay = /* @__PURE__ */ new Date(targetDateStr + "T00:00:00Z");
  const diffMs = targetDay.getTime() - currentDay.getTime();
  return Math.floor(diffMs / (24 * 60 * 60 * 1e3));
}
function getFormatFamily(style) {
  return style === "words-long" || style === "words-short" ? "labeled" : "positional";
}
function decomposeCountdown(diff, granularity) {
  const SEC = 1e3;
  const MIN = 60 * SEC;
  const HRS = 60 * MIN;
  const DAYS = 24 * HRS;
  const totalDays = Math.floor(diff / DAYS);
  const totalHours = Math.floor(diff / HRS);
  const hours = totalHours - totalDays * 24;
  const minutes = Math.floor(diff % HRS / MIN);
  const seconds = Math.floor(diff % MIN / SEC);
  const units = [];
  const includeDays = granularity.includes("D");
  const includeHours = granularity.includes("H");
  const includeMinutes = granularity.includes("M");
  const includeSeconds = granularity.includes("S");
  if (includeDays)
    units.push({ type: "days", value: totalDays });
  if (includeHours)
    units.push({ type: "hours", value: includeDays ? hours : totalHours });
  if (includeMinutes)
    units.push({ type: "minutes", value: minutes });
  if (includeSeconds)
    units.push({ type: "seconds", value: seconds });
  return units;
}
function suppressLeadingZeros(units, family) {
  if (units.length === 0)
    return units;
  if (family === "labeled") {
    const firstNonZero = units.findIndex((u) => u.value !== 0);
    return firstNonZero === -1 ? [] : units.slice(firstNonZero);
  }
  if (units[0].type === "days" && units[0].value === 0) {
    return units.slice(1);
  }
  return units;
}
function fmtCountdownV2(targetDate, currentDate, formatStyle, timeGranularity, timezone, locale) {
  const diff = targetDate.getTime() - currentDate.getTime();
  const t = (key) => getLocalizedText(key, locale);
  if (diff <= 0) {
    return t("live");
  }
  const calendarDays = getCalendarDaysDiff(currentDate, targetDate, timezone);
  if (timeGranularity === "D") {
    if (calendarDays === 0)
      return t("today");
    return formatDaysOnly(calendarDays, formatStyle, t);
  }
  const units = decomposeCountdown(diff, timeGranularity);
  if (units.every((u) => u.value === 0)) {
    return t("startingSoon");
  }
  const family = getFormatFamily(formatStyle);
  const filtered = suppressLeadingZeros(units, family);
  const pluralize = (count, singular, plural) => count === 1 ? t(singular) : t(plural);
  switch (formatStyle) {
    case "digital":
      return formatDigital(filtered);
    case "words-long":
      return formatWordsLong(filtered, pluralize);
    case "words-short":
      return formatWordsShort(filtered, t);
    case "days-digital":
      return formatDaysDigital(filtered, pluralize);
    default:
      return formatDigital(filtered);
  }
}
function formatDaysOnly(calendarDays, formatStyle, t) {
  const pluralize = (count, singular, plural) => count === 1 ? t(singular) : t(plural);
  switch (formatStyle) {
    case "digital":
      return `${calendarDays}`;
    case "words-short":
      return `${calendarDays}${t("dayShort")}`;
    case "words-long":
    case "days-digital":
    default:
      return `${calendarDays} ${pluralize(calendarDays, "day", "days")}`;
  }
}
function formatDigital(units) {
  return units.map((u, i) => i === 0 ? String(u.value) : u.value.toString().padStart(2, "0")).join(":");
}
function formatWordsLong(units, pluralize) {
  const labelMap = {
    days: ["day", "days"],
    hours: ["hour", "hours"],
    minutes: ["minute", "minutes"],
    seconds: ["second", "seconds"]
  };
  return units.map((u) => `${u.value} ${pluralize(u.value, ...labelMap[u.type])}`).join(" ");
}
function formatWordsShort(units, t) {
  const labelMap = {
    days: t("dayShort"),
    hours: t("hourShort"),
    minutes: t("minuteShort"),
    seconds: t("secondShort")
  };
  return units.map((u) => `${u.value}${labelMap[u.type]}`).join(" ");
}
function formatDaysDigital(units, pluralize) {
  const daysUnit = units.find((u) => u.type === "days");
  const clockUnits = units.filter((u) => u.type !== "days");
  const clockStr = clockUnits.map((u, i) => {
    if (i === 0 && !daysUnit)
      return String(u.value);
    return u.value.toString().padStart(2, "0");
  }).join(":");
  if (daysUnit) {
    const daysStr = `${daysUnit.value} ${pluralize(daysUnit.value, "day", "days")}`;
    return clockStr ? `${daysStr} ${clockStr}` : daysStr;
  }
  return clockStr;
}
function fmtTime(date, locale, timeZone = "Etc/UTC", hour12 = true, showTimezone = false) {
  let options = {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    hour12
  };
  if (showTimezone) {
    options = {
      ...options,
      timeZoneName: "short"
    };
  }
  return date.toLocaleTimeString(locale, options);
}
function fmtMonth(date, locale, timeZone = "Etc/UTC", monthFormat = "long" /* LONG */) {
  const month = date.toLocaleDateString(locale, {
    month: monthFormat,
    timeZone
  });
  return month.at(0)?.toUpperCase() + month.slice(1);
}

// src/feature-flag-context.tsx
import React2, {
  useState as useState2,
  useContext as useContext2
} from "react";
import { jsx } from "react/jsx-runtime";
var FeatureFlagContext = React2.createContext({
  flags: {
    useMaterialTheme: false,
    allowFontUpload: false
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFlags: () => {
  }
});
var FeatureFlagProvider = ({ children }) => {
  const params = new URLSearchParams(window.location.search);
  const useMaterialTheme = params.get("useMaterialTheme") === "true";
  const allowFontUpload = params.get("allowFontUpload") === "true";
  const [flags, setFlags] = useState2({
    useMaterialTheme,
    allowFontUpload
  });
  return /* @__PURE__ */ jsx(FeatureFlagContext.Provider, { value: { flags, setFlags }, children });
};
function useFeatureFlags() {
  const context = useContext2(FeatureFlagContext);
  if (context === void 0) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context;
}

// src/parseFonts.ts
var parseFontWeight = (fontWeight) => {
  const fontWeightLowerCase = fontWeight.toLowerCase();
  if (fontWeightLowerCase.includes("thin") || fontWeightLowerCase.includes("hairline")) {
    return 100;
  }
  if (fontWeightLowerCase.includes("extra light") || fontWeightLowerCase.includes("ultra light")) {
    return 200;
  }
  if (fontWeightLowerCase.includes("light")) {
    return 300;
  }
  if (fontWeightLowerCase.includes("normal") || fontWeightLowerCase.includes("regular")) {
    return 400;
  }
  if (fontWeightLowerCase.includes("medium")) {
    return 500;
  }
  if (fontWeightLowerCase.includes("semi bold") || fontWeightLowerCase.includes("demi bold")) {
    return 600;
  }
  if (fontWeightLowerCase.includes("extra bold") || fontWeightLowerCase.includes("ultra bold")) {
    return 800;
  }
  if (fontWeightLowerCase.includes("bold")) {
    return 700;
  }
  if (fontWeightLowerCase.includes("extra black") || fontWeightLowerCase.includes("ultra black")) {
    return 950;
  }
  if (fontWeightLowerCase.includes("black") || fontWeightLowerCase.includes("heavy")) {
    return 900;
  }
  return 400;
};
var parseFontStyle = (str) => {
  const strLower = str.toLowerCase();
  if (strLower.includes("normal")) {
    return "normal";
  }
  if (strLower.includes("italic")) {
    return "italic";
  }
  if (strLower.includes("oblique")) {
    return "oblique";
  }
  return "normal";
};
function notEmpty(value) {
  return value !== null && value !== void 0;
}
var parseFontStretch = (variant) => {
  const variantLower = variant.toLowerCase();
  const stretchVariants = [
    { keywords: ["ultra condensed", "ultracondensed"], value: "ultra-condensed" },
    { keywords: ["extra condensed", "extracondensed"], value: "extra-condensed" },
    { keywords: ["semi condensed", "semicondensed"], value: "semi-condensed" },
    { keywords: ["condensed", "narrow", "compressed"], value: "condensed" },
    { keywords: ["semi expanded", "semiexpanded"], value: "semi-expanded" },
    { keywords: ["extra expanded", "extraexpanded"], value: "extra-expanded" },
    { keywords: ["ultra expanded", "ultraexpanded"], value: "ultra-expanded" },
    { keywords: ["expanded", "wide"], value: "expanded" }
  ];
  for (const { keywords, value } of stretchVariants) {
    if (keywords.some((keyword) => variantLower.includes(keyword))) {
      return value;
    }
  }
  return "normal";
};
var parseFonts = (fonts) => {
  return fonts.flatMap((item) => {
    const baseFontFamily = item?.name.trim() ?? "";
    const fontFamilyId = item?.id;
    const isDefault = item.holdingId === -1;
    return item?.fonts?.map((font) => {
      const variant = font?.variant ?? "";
      return {
        fontFamily: baseFontFamily,
        fontFamilyId,
        // Use structured fields from DB if available, otherwise parse from variant string
        weight: font?.weight ?? parseFontWeight(variant),
        style: font?.style ?? parseFontStyle(variant),
        stretch: font?.stretch ?? parseFontStretch(variant),
        variant: font?.variant ?? void 0,
        src: font?.path ?? "",
        isDefault
      };
    }) ?? [];
  }) ?? [];
};

// src/fontVariantMatching.ts
function getWeightLabel(weight) {
  const weightMap = {
    100: "Thin",
    200: "Extra Light",
    300: "Light",
    400: "Regular",
    500: "Medium",
    600: "Semi Bold",
    700: "Bold",
    800: "Extra Bold",
    900: "Black",
    950: "Extra Black"
  };
  if (weightMap[weight]) {
    return weightMap[weight];
  }
  const weights = Object.keys(weightMap).map(Number).sort((a, b) => a - b);
  const closest = weights.reduce(
    (prev, curr) => Math.abs(curr - weight) < Math.abs(prev - weight) ? curr : prev
  );
  return `${weightMap[closest]} (${weight})`;
}
function findClosestByWeight(variants, targetWeight) {
  return variants.reduce((closest, current) => {
    const currentDiff = Math.abs(current.weight - targetWeight);
    const closestDiff = Math.abs(closest.weight - targetWeight);
    if (currentDiff < closestDiff) {
      return current;
    }
    if (currentDiff === closestDiff && current.weight < closest.weight) {
      return current;
    }
    return closest;
  });
}
function findClosestFontVariant(variants, targetWeight = 400, targetStyle = "normal", targetStretch = "normal") {
  if (!variants || variants.length === 0) {
    return {
      variant: null,
      isExactMatch: false,
      mismatchMessage: "No font variants available"
    };
  }
  const normalizedTargetStyle = targetStyle || "normal";
  const normalizedTargetWeight = targetWeight || 400;
  const normalizedTargetStretch = targetStretch || "normal";
  const exactMatch = variants.find(
    (v) => v.weight === normalizedTargetWeight && v.style === normalizedTargetStyle && v.stretch === normalizedTargetStretch
  );
  if (exactMatch) {
    return {
      variant: exactMatch,
      isExactMatch: true,
      mismatchMessage: null
    };
  }
  const matchIgnoringStretch = variants.find(
    (v) => v.weight === normalizedTargetWeight && v.style === normalizedTargetStyle
  );
  if (matchIgnoringStretch) {
    return {
      variant: matchIgnoringStretch,
      isExactMatch: true,
      // Consider this exact since stretch is often not critical
      mismatchMessage: null
    };
  }
  const sameStyleVariants = variants.filter((v) => v.style === normalizedTargetStyle);
  if (sameStyleVariants.length > 0) {
    const closestByWeight = findClosestByWeight(sameStyleVariants, normalizedTargetWeight);
    const weightLabel2 = getWeightLabel(normalizedTargetWeight);
    const matchedWeightLabel = getWeightLabel(closestByWeight.weight);
    return {
      variant: closestByWeight,
      isExactMatch: false,
      mismatchMessage: `${weightLabel2} is not available, using ${matchedWeightLabel} instead`
    };
  }
  const closestOverall = findClosestByWeight(variants, normalizedTargetWeight);
  const weightLabel = getWeightLabel(normalizedTargetWeight);
  const styleLabel = normalizedTargetStyle === "normal" ? "" : ` ${normalizedTargetStyle}`;
  return {
    variant: closestOverall,
    isExactMatch: false,
    mismatchMessage: `${weightLabel}${styleLabel} is not available`
  };
}
function getVariantLabel(weight, style, variant) {
  if (variant)
    return variant;
  const weightLabel = getWeightLabel(weight);
  const styleLabel = style !== "normal" ? ` ${style.charAt(0).toUpperCase() + style.slice(1)}` : "";
  return `${weightLabel}${styleLabel}`;
}
function findBestMatchingCustomFont(customFonts, fontFamily, targetWeight = 400, targetStyle = "normal", targetStretch = "normal") {
  const fontsForFamily = customFonts.filter((f) => f.fontFamily === fontFamily);
  if (fontsForFamily.length === 0) {
    return {
      font: null,
      isExactMatch: false,
      mismatchMessage: `Font family "${fontFamily}" is not available in your Brand Kit`
    };
  }
  const variants = fontsForFamily.map((f) => ({
    weight: f.weight ?? 400,
    style: f.style ?? "normal",
    stretch: f.stretch ?? "normal",
    variant: f.variant,
    label: getVariantLabel(f.weight ?? 400, f.style ?? "normal", f.variant)
  }));
  const matchResult = findClosestFontVariant(variants, targetWeight, targetStyle, targetStretch);
  if (!matchResult.variant) {
    return {
      font: null,
      isExactMatch: false,
      mismatchMessage: matchResult.mismatchMessage
    };
  }
  const matchedVariant = matchResult.variant;
  const matchedFont = fontsForFamily.find(
    (f) => (f.weight ?? 400) === matchedVariant.weight && (f.style ?? "normal") === matchedVariant.style && (f.stretch ?? "normal") === matchedVariant.stretch
  );
  if (!matchedFont) {
    const firstFont = fontsForFamily[0];
    return {
      font: {
        fontFamily: firstFont.fontFamily,
        weight: firstFont.weight ?? 400,
        style: firstFont.style ?? "normal",
        stretch: firstFont.stretch ?? "normal",
        variant: firstFont.variant,
        src: firstFont.src
      },
      isExactMatch: false,
      mismatchMessage: matchResult.mismatchMessage
    };
  }
  return {
    font: {
      fontFamily: matchedFont.fontFamily,
      weight: matchedFont.weight ?? 400,
      style: matchedFont.style ?? "normal",
      stretch: matchedFont.stretch ?? "normal",
      variant: matchedFont.variant,
      src: matchedFont.src
    },
    isExactMatch: matchResult.isExactMatch,
    mismatchMessage: matchResult.mismatchMessage
  };
}
function formatFontMismatchMessage(fontFamily, targetWeight, targetStyle = "normal") {
  const weightLabel = getWeightLabel(targetWeight);
  const styleLabel = targetStyle !== "normal" ? ` ${targetStyle}` : "";
  return `${fontFamily} ${weightLabel}${styleLabel} is not available in your Brand Kit`;
}
function applyFontVariantAdjustments(elements, parsedFonts, primaryFontFamilyName, secondaryFontFamilyName) {
  const adjustments = /* @__PURE__ */ new Map();
  const adjustedElements = elements.map((element) => {
    if (element.type !== "TEXT") {
      return element;
    }
    const data = element.data;
    if (!data?.style) {
      return element;
    }
    const style = data.style;
    const fontFamily = style.fontFamily;
    const fontWeight = style.fontWeight ?? 400;
    const fontStyle = style.fontStyle ?? "normal";
    if (!fontFamily)
      return element;
    const isPrimaryFont = primaryFontFamilyName && fontFamily === primaryFontFamilyName;
    const isSecondaryFont = secondaryFontFamilyName && fontFamily === secondaryFontFamilyName;
    if (!isPrimaryFont && !isSecondaryFont) {
      return element;
    }
    const cacheKey = `${fontFamily}-${fontWeight}-${fontStyle}`;
    const cachedAdjustment = adjustments.get(cacheKey);
    if (cachedAdjustment) {
      return {
        ...element,
        data: {
          ...data,
          style: {
            ...style,
            fontWeight: cachedAdjustment.fontWeight,
            fontStyle: cachedAdjustment.fontStyle
          }
        }
      };
    }
    const matchResult = findBestMatchingCustomFont(
      parsedFonts,
      fontFamily,
      fontWeight,
      fontStyle,
      "normal"
      // stretch defaults to normal
    );
    if (matchResult.font) {
      const adjustment = {
        fontWeight: matchResult.font.weight,
        fontStyle: matchResult.font.style
      };
      if (!matchResult.isExactMatch) {
        adjustments.set(cacheKey, adjustment);
      }
      return {
        ...element,
        data: {
          ...data,
          style: {
            ...style,
            fontWeight: matchResult.font.weight,
            fontStyle: matchResult.font.style
          }
        }
      };
    }
    return element;
  });
  return { adjustedElements, adjustments };
}

// src/auth-aws-cognito/cognitoAuth.ts
var CognitoAuth = class {
  tokenEndpoint;
  refreshToken;
  idToken;
  constructor(tokenEndpoint, refreshToken, idToken) {
    if (!tokenEndpoint) {
      throw new Error("tokenEndpoint is not provided");
    }
    if (!refreshToken) {
      throw new Error("refreshToken is not provided");
    }
    if (!idToken) {
      throw new Error("idToken is not provided");
    }
    this.tokenEndpoint = tokenEndpoint;
    this.refreshToken = refreshToken;
    this.idToken = idToken;
  }
  async refreshIDToken(refreshToken, idToken) {
    const body = JSON.stringify({ refreshToken });
    const response = await fetch(this.tokenEndpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
        Accept: "*/*"
      },
      body
    });
    if (!response.ok) {
      const responseBody = await response.text();
      throw new Error(
        `failed to refresh token, status: ${response.status}, response: ${responseBody}`
      );
    }
    return response.json();
  }
  async getToken() {
    const tokenResponse = await this.refreshIDToken(this.refreshToken, this.idToken);
    return tokenResponse.idToken;
  }
};

// src/context/UserContext.ts
import { createContext as createContext2 } from "react";
var UserContext = createContext2({ user: null, setUser: () => null });
UserContext.displayName = "UserContext";
var Provider = UserContext.Provider;
var Consumer = UserContext.Consumer;
var UserContext_default = UserContext;

// src/currencies.ts
var currencies = [
  {
    name: "BGN",
    value: "BGN"
  },
  {
    name: "BRL",
    value: "BRL"
  },
  {
    name: "CDF",
    value: "CDF"
  },
  {
    name: "ETB",
    value: "ETB"
  },
  {
    name: "EUR",
    value: "EUR"
  },
  {
    name: "GBP",
    value: "GBP"
  },
  {
    name: "GHS",
    value: "GHS"
  },
  {
    name: "INR",
    value: "INR"
  },
  {
    name: "KES",
    value: "KES"
  },
  {
    name: "MMK",
    value: "MMK"
  },
  {
    name: "MZN",
    value: "MZN"
  },
  {
    name: "NGN",
    value: "NGN"
  },
  {
    name: "RUB",
    value: "RUB"
  },
  {
    name: "SEK",
    value: "SEK"
  },
  {
    name: "TZS",
    value: "TZS"
  },
  {
    name: "USD",
    value: "USD"
  },
  {
    name: "ZMK",
    value: "ZMK"
  },
  {
    name: "CHF",
    value: "CHF"
  }
];

// src/element-mapping.ts
import { v4 as uuidv4 } from "uuid";
import {
  Datasources,
  isTextElement as isTextElement2,
  isImageElement,
  isSVGElement as isSVGElement2,
  BrandVariable,
  IMAGE_VARIABLE_MAPPINGS,
  parseElementName,
  buildCustomAssetContentUrl,
  hasInlineTokens as hasInlineTokens2,
  INLINE_TOKEN_REGEX
} from "@adflow/types";

// src/svg-utils.ts
import {
  hasInlineTokens,
  isSVGElement,
  isTextElement,
  isVectorElement
} from "@adflow/types";
function removeElementByName(svg, elementName) {
  const escapedName = elementName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const openTagRegex = new RegExp(
    `<([a-zA-Z][a-zA-Z0-9:.-]*)[^>]*id="(?:[^"]*--)?${escapedName}"[^>]*>`
  );
  const match = openTagRegex.exec(svg);
  if (!match)
    return svg;
  const tagName = match[1];
  const startIndex = match.index;
  const matchEnd = startIndex + match[0].length;
  const isSelfClosing = /\/\s*>$/.test(match[0]);
  const hasMalformedSelfClose = /(?:["']|\s)\/\s+[a-zA-Z_][\w:.-]*\s*=/.test(match[0]);
  const hasExplicitClosingTag = svg.slice(matchEnd).includes(`</${tagName}>`);
  if (isSelfClosing || hasMalformedSelfClose && !hasExplicitClosingTag) {
    return svg.slice(0, startIndex) + svg.slice(matchEnd);
  }
  const closeTagStr = `</${tagName}>`;
  let depth = 1;
  let pos = matchEnd;
  while (depth > 0 && pos < svg.length) {
    const openIdx = svg.indexOf(`<${tagName}`, pos);
    const closeIdx = svg.indexOf(closeTagStr, pos);
    if (closeIdx === -1)
      break;
    if (openIdx !== -1 && openIdx < closeIdx) {
      const charAfterTag = svg[openIdx + tagName.length + 1];
      if (" 	\n\r/>".includes(charAfterTag)) {
        depth++;
      }
      pos = openIdx + 1;
    } else {
      depth--;
      if (depth === 0) {
        return svg.slice(0, startIndex) + svg.slice(closeIdx + closeTagStr.length);
      }
      pos = closeIdx + 1;
    }
  }
  return svg;
}
function stripRootFrameBackgroundFromSvg(svg, frameName) {
  const escapedName = frameName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const openTagRegex = new RegExp(
    `<([a-zA-Z][a-zA-Z0-9:.-]*)[^>]*id="(?:[^"]*--)?${escapedName}"[^>]*>`
  );
  const match = openTagRegex.exec(svg);
  if (!match)
    return svg;
  const tagName = match[1];
  const startIndex = match.index;
  const matchEnd = startIndex + match[0].length;
  const isSelfClosing = /\/\s*>$/.test(match[0]);
  const hasMalformedSelfClose = /(?:["']|\s)\/\s+[a-zA-Z_][\w:.-]*\s*=/.test(match[0]);
  const hasExplicitClosingTag = svg.slice(matchEnd).includes(`</${tagName}>`);
  if (isSelfClosing || hasMalformedSelfClose && !hasExplicitClosingTag || tagName.toLowerCase() !== "g") {
    return removeElementByName(svg, frameName);
  }
  const closeTagStr = `</${tagName}>`;
  let depth = 1;
  let pos = matchEnd;
  while (depth > 0 && pos < svg.length) {
    const openIdx = svg.indexOf(`<${tagName}`, pos);
    const closeIdx = svg.indexOf(closeTagStr, pos);
    if (closeIdx === -1)
      break;
    if (openIdx !== -1 && openIdx < closeIdx) {
      const charAfterTag = svg[openIdx + tagName.length + 1];
      if (" 	\n\r/>".includes(charAfterTag)) {
        depth++;
      }
      pos = openIdx + 1;
    } else {
      depth--;
      if (depth === 0) {
        const innerContent = svg.slice(matchEnd, closeIdx);
        const unwrappedInner = innerContent.replace(/^\s*<rect\b[^>]*\/>/i, "").replace(/^\s*<rect\b[^>]*>[\s\S]*?<\/rect>/i, "");
        return svg.slice(0, startIndex) + unwrappedInner + svg.slice(closeIdx + closeTagStr.length);
      }
      pos = closeIdx + 1;
    }
  }
  return svg;
}
function removeHiddenFigmaElements(svg) {
  return svg.replace(/<[a-zA-Z][^>]*["']\/\s+display="none">/g, "");
}
function removeOrphanedDefsImages(svg) {
  const referencedPatternIds = /* @__PURE__ */ new Set();
  for (const m of svg.matchAll(/url\((['"]?)#([^)'" ]+)\1\)/g)) {
    referencedPatternIds.add(m[2]);
  }
  let result = svg.replace(
    /<pattern\b[^>]*id="([^"]+)"[^>]*>[\s\S]*?<\/pattern>/g,
    (match, id) => referencedPatternIds.has(id) ? match : ""
  );
  const neededImageIds = /* @__PURE__ */ new Set();
  for (const patternId of referencedPatternIds) {
    const escaped = patternId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const patternMatch = new RegExp(
      `<pattern[^>]*id="${escaped}"[^>]*>[\\s\\S]*?<\\/pattern>`
    ).exec(result);
    if (patternMatch) {
      for (const m of patternMatch[0].matchAll(/(?:xlink:href|href)="#([^"]+)"/g)) {
        neededImageIds.add(m[1]);
      }
    }
  }
  result = result.replace(
    /<image\b[^>]*id="([^"]+)"[^>]*(?:\/>|>[\s\S]*?<\/image>)/g,
    (match, id) => neededImageIds.has(id) ? match : ""
  );
  result = result.replace(/<defs>\s*<\/defs>/g, "<defs>\n  </defs>");
  result = result.replace(/\n(?:[ \t]*\n)+(\s*<\/svg>\s*)$/, "\n$1");
  return result;
}
function cleanSVGElement(svgElement, elementsToStrip) {
  if (!isSVGElement(svgElement) || !svgElement.data?.svg) {
    return svgElement;
  }
  let cleanedSvg = svgElement.data.svg;
  cleanedSvg = removeHiddenFigmaElements(cleanedSvg);
  elementsToStrip.forEach((elementToStrip) => {
    const svgBeforeStrip = cleanedSvg;
    cleanedSvg = removeElementByName(cleanedSvg, elementToStrip.name);
    let wasStripped = cleanedSvg !== svgBeforeStrip;
    if (!wasStripped && elementToStrip.id.endsWith("-bg")) {
      const frameName = elementToStrip.name.replace(/ Background$/, "");
      if (frameName !== elementToStrip.name) {
        const svgBeforeFrameStrip = cleanedSvg;
        cleanedSvg = stripRootFrameBackgroundFromSvg(cleanedSvg, frameName);
        wasStripped = cleanedSvg !== svgBeforeFrameStrip;
      }
    }
    if (!wasStripped && process.env.NODE_ENV !== "production" && isTextElement(elementToStrip) && (hasInlineTokens(elementToStrip.name) || hasInlineTokens(elementToStrip.data?.content))) {
      console.warn(
        `[svg-utils] Templated text layer "${elementToStrip.name}" was not found by id in the exported SVG. The baked-in mock copy may bleed through at render time. Check for unusual characters (quotes, newlines) in the layer name.`
      );
    }
  });
  cleanedSvg = removeOrphanedDefsImages(cleanedSvg);
  return {
    ...svgElement,
    data: {
      ...svgElement.data,
      svg: cleanedSvg
    }
  };
}
function stripOverlayDuplicatesFromSvg(adElements) {
  const enrichedElements = enrichRootBackgroundVectors(adElements);
  const svgIndex = enrichedElements.findIndex((element) => isSVGElement(element));
  if (svgIndex === -1) {
    return enrichedElements;
  }
  const svgElement = enrichedElements[svgIndex];
  const overlayElements = enrichedElements.filter((_, index) => index !== svgIndex);
  const cleanedSvgElement = cleanSVGElement(svgElement, overlayElements);
  if (cleanedSvgElement.type !== "SVG" || svgElement.type !== "SVG") {
    return enrichedElements;
  }
  if (cleanedSvgElement.data.svg === svgElement.data.svg) {
    return enrichedElements;
  }
  const cleanedElements = enrichedElements.slice();
  cleanedElements[svgIndex] = cleanedSvgElement;
  return cleanedElements;
}
function enrichRootBackgroundVector(vector, mainSvg) {
  const width = vector.size.width || vector.data.viewBox.width;
  const height = vector.size.height || vector.data.viewBox.height;
  let content = vector.data.content;
  if (!content) {
    content = extractSVGContentForElement(mainSvg, vector.name) ?? extractSVGContentForElement(mainSvg, vector.name.replace(/ Background$/, "")) ?? (width && height ? `<rect x="0" y="0" width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"/>` : "");
  }
  if (content) {
    content = sanitizeVectorOverlayContent(content, {
      x: vector.position.x,
      y: vector.position.y,
      width,
      height
    });
  }
  if (width > 0 && height > 0) {
    content = `<rect x="0" y="0" width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg"/>`;
  }
  if (!content) {
    return vector;
  }
  return {
    ...vector,
    data: {
      ...vector.data,
      content,
      viewBox: {
        width,
        height
      }
    }
  };
}
function enrichRootBackgroundVectors(adElements, mainSvgOverride) {
  const svgElement = adElements.find(isSVGElement);
  const mainSvg = mainSvgOverride ?? (svgElement?.type === "SVG" ? svgElement.data.svg : "");
  let changed = false;
  const result = adElements.map((element) => {
    if (!isVectorElement(element)) {
      return element;
    }
    const enriched = element.id.endsWith("-bg") ? enrichRootBackgroundVector(element, mainSvg) : element;
    if (enriched !== element) {
      changed = true;
    }
    if (!enriched.data.content) {
      return enriched;
    }
    const sanitized = sanitizeVectorOverlayContent(enriched.data.content, {
      x: enriched.position.x,
      y: enriched.position.y,
      width: enriched.size.width,
      height: enriched.size.height
    });
    if (sanitized === enriched.data.content) {
      return enriched;
    }
    changed = true;
    return {
      ...enriched,
      data: {
        ...enriched.data,
        content: sanitized
      }
    };
  });
  return changed ? result : adElements;
}
function stripTextNodesFromSvgContent(content) {
  return content.replace(/<text\b[^>]*>[\s\S]*?<\/text>/gi, "").replace(/<text\b[^>]*\/>/gi, "");
}
function isolateBackgroundShapeContent(content) {
  const selfClosingRect = content.match(/<rect\b[^>]*\/>/i);
  if (selfClosingRect) {
    return selfClosingRect[0];
  }
  const rectWithBody = content.match(/<rect\b[^>]*>[\s\S]*?<\/rect>/i);
  if (rectWithBody) {
    return rectWithBody[0];
  }
  return content;
}
function buildBackgroundRect(bounds) {
  return `<rect x="${bounds.x}" y="${bounds.y}" width="${bounds.width}" height="${bounds.height}" xmlns="http://www.w3.org/2000/svg"/>`;
}
function sanitizeVectorOverlayContent(content, bounds) {
  let sanitized = stripTextNodesFromSvgContent(content);
  if (/<g[\s>]/i.test(sanitized) && bounds && bounds.width > 0 && bounds.height > 0) {
    return buildBackgroundRect(bounds);
  }
  if (/<g[\s>]/i.test(sanitized)) {
    sanitized = isolateBackgroundShapeContent(sanitized);
  }
  return sanitized;
}
function extractSVGContentForElement(svg, elementName) {
  const escapedName = elementName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(
    `(<(path|g|rect|circle|ellipse|line|polyline|polygon)[^>]*id="(?:[^"]*--)?${escapedName}"[^>]*>(?:[\\s\\S]*?<\\/\\2>|))`,
    "g"
  );
  const match = regex.exec(svg);
  if (match) {
    let content = match[1];
    if (!content.includes("xmlns=")) {
      content = content.replace(/^<([^ >]+)/, '<$1 xmlns="http://www.w3.org/2000/svg"');
    }
    return content;
  }
  return null;
}
function scopeSvgIds(svgString, prefix) {
  if (!svgString || !prefix) {
    return svgString;
  }
  let scoped = svgString.replace(/id="([^"]+)"/g, `id="${prefix}_$1"`);
  scoped = scoped.replace(/url\(#([^)]+)\)/g, `url(#${prefix}_$1)`);
  scoped = scoped.replace(/xlink:href="#([^"]+)"/g, `xlink:href="#${prefix}_$1"`);
  scoped = scoped.replace(/(?<!xlink:)href="#([^"]+)"/g, `href="#${prefix}_$1"`);
  return scoped;
}
var svgUtils = {
  cleanSVGElement,
  stripOverlayDuplicatesFromSvg,
  enrichRootBackgroundVectors,
  extractSVGContentForElement,
  sanitizeVectorOverlayContent,
  scopeSvgIds
};

// src/element-mapping.ts
function mapConceptDataSourceToAdTemplateDataSources(dataSource) {
  const technicalSources = [];
  switch (dataSource.dataSource) {
    case "SPORTS_MOMENTS": {
      technicalSources.push({
        id: uuidv4(),
        type: dataSource.competition
      });
      const stageSuffixes = {
        PreEvent: "_PRE_EVENT",
        Live: "_LIVE",
        PostEvent: "_POST_EVENT"
      };
      const stageSuffix = stageSuffixes[dataSource.stage];
      if (stageSuffix) {
        technicalSources.push({
          id: uuidv4(),
          type: `${dataSource.competition}${stageSuffix}`
        });
      }
      break;
    }
    case "SPORTS": {
      const rawTemplateType = dataSource.templateType ?? "";
      const normalizedTemplateType = rawTemplateType.toUpperCase() === "EVENT" ? "NEW_EVENT" : rawTemplateType;
      technicalSources.push({
        id: uuidv4(),
        type: normalizedTemplateType
      });
      if (dataSource.marketTypes && dataSource.marketTypes.length > 0) {
        dataSource.marketTypes.forEach((market) => {
          technicalSources.push({
            id: uuidv4(),
            type: market
          });
        });
      }
      break;
    }
    case "NASCAR": {
      technicalSources.push({
        id: uuidv4(),
        type: "NASCAR"
      });
      break;
    }
    default: {
      technicalSources.push({
        id: uuidv4(),
        type: "UNKNOWN"
      });
    }
  }
  return technicalSources;
}
function mapElements(figmaComponent, suggestedDataSources, dataSources, dataSourceMappings, customizationDetails, brandFonts, svgIdPrefix) {
  const mainSvg = figmaComponent.svgElement?.data.svg || "";
  let backgroundSVG = figmaComponent.svgElement ? svgUtils.cleanSVGElement(figmaComponent.svgElement, [
    ...figmaComponent.textElements,
    ...figmaComponent.imageElements,
    ...figmaComponent.vectorElements
  ]) : void 0;
  if (backgroundSVG && svgIdPrefix && isSVGElement2(backgroundSVG) && backgroundSVG.data.svg) {
    backgroundSVG = {
      ...backgroundSVG,
      data: {
        ...backgroundSVG.data,
        svg: svgUtils.scopeSvgIds(backgroundSVG.data.svg, svgIdPrefix)
      }
    };
  }
  const elements = [
    ...backgroundSVG ? [backgroundSVG] : [],
    ...figmaComponent.textElements.map((el) => ({ ...el, name: parseElementName(el.name).name })),
    ...figmaComponent.imageElements,
    ...figmaComponent.vectorElements.map((vector) => {
      if (!vector.data.content || vector.data.content === "") {
        let content = svgUtils.extractSVGContentForElement(mainSvg, vector.name);
        if (!content && vector.id.endsWith("-bg")) {
          content = svgUtils.extractSVGContentForElement(
            mainSvg,
            vector.name.replace(/ Background$/, "")
          );
        }
        if (content) {
          content = svgUtils.sanitizeVectorOverlayContent(content, {
            x: vector.position.x,
            y: vector.position.y,
            width: vector.size.width,
            height: vector.size.height
          });
        }
        if (content && svgIdPrefix) {
          content = svgUtils.scopeSvgIds(content, svgIdPrefix);
        }
        if (content) {
          return {
            ...vector,
            data: {
              ...vector.data,
              content,
              viewBox: {
                width: figmaComponent.size?.width || 0,
                height: figmaComponent.size?.height || 0
              },
              // Also scope effect filters if present
              ...vector.data.effectFilters && svgIdPrefix ? { effectFilters: svgUtils.scopeSvgIds(vector.data.effectFilters, svgIdPrefix) } : {}
            }
          };
        }
      }
      if (svgIdPrefix && vector.data.effectFilters) {
        return {
          ...vector,
          data: {
            ...vector.data,
            effectFilters: svgUtils.scopeSvgIds(vector.data.effectFilters, svgIdPrefix)
          }
        };
      }
      return vector;
    })
  ];
  const enrichedElements = svgUtils.enrichRootBackgroundVectors(elements, mainSvg);
  return enrichedElements.map((element) => {
    let updatedElement = JSON.parse(JSON.stringify(element));
    if (isTextElement2(updatedElement) && updatedElement.data.style?.fontFamily && brandFonts) {
      const fontFamily = updatedElement.data.style.fontFamily;
      if (fontFamily === BrandVariable.PRIMARY_FONT && brandFonts.primaryFontName) {
        updatedElement.data.style.fontFamily = brandFonts.primaryFontName;
      } else if (fontFamily === BrandVariable.SECONDARY_FONT && brandFonts.secondaryFontName) {
        updatedElement.data.style.fontFamily = brandFonts.secondaryFontName;
      }
    }
    if (isTextElement2(updatedElement) && hasInlineTokens2(updatedElement.data.content)) {
      const newSources = [];
      updatedElement.data.content = updatedElement.data.content.replace(
        INLINE_TOKEN_REGEX,
        (raw, token) => {
          const match = Datasources.resolveTokenToDataPoint(token, dataSources);
          if (!match) {
            if (process.env.NODE_ENV !== "production") {
              console.warn(
                `[element-mapping] Unknown inline token {${token}} in template "${element.name}" \u2014 leaving literal.`
              );
            }
            return raw;
          }
          if (!newSources.some((s) => s.sourceId === match.sourceId && s.name === match.dataPointName)) {
            newSources.push({ sourceId: match.sourceId, name: match.dataPointName });
          }
          return `{${match.sourceId}:${match.dataPointName}}`;
        }
      );
      updatedElement.sources = Datasources.mergeTextElementSources(
        updatedElement.sources,
        newSources
      );
      return updatedElement;
    }
    const { name: cleanName } = parseElementName(element.name);
    if (customizationDetails && customizationDetails[cleanName] && isTextElement2(updatedElement)) {
      updatedElement.data.content = customizationDetails[cleanName];
      return updatedElement;
    }
    const explicitMapping = dataSourceMappings?.[element.name];
    if (explicitMapping) {
      const [sourcePrefix, dataPointName] = explicitMapping.split(".");
      const matchingSource = dataSources.find((source) => {
        if (source.type === sourcePrefix)
          return true;
        if (sourcePrefix === "EVENT" || sourcePrefix === "NEW_EVENT") {
          return Datasources.getDataTypeFromSourceType(source.type) === "TEMPLATE";
        }
        return false;
      });
      if (matchingSource && dataPointName) {
        const normalizedDPName = dataPointName.replace(/([a-z])([A-Z])/g, "$1_$2").toUpperCase();
        return applyDataPointToElement(updatedElement, matchingSource, normalizedDPName);
      } else {
        if (isTextElement2(updatedElement) || isImageElement(updatedElement)) {
          updatedElement.data.content = `{{${explicitMapping}}}`;
        }
        return updatedElement;
      }
    }
    const elementSuggestions = suggestedDataSources.flat().filter((dataPoint) => dataPoint.elId === element.id);
    if (elementSuggestions.length === 0) {
      const brandVariableName = Object.entries(IMAGE_VARIABLE_MAPPINGS).find(
        ([key]) => updatedElement.name.toUpperCase().startsWith(key)
      )?.[1];
      if (brandVariableName && isImageElement(updatedElement)) {
        updatedElement.data.content = brandVariableName;
      }
      return updatedElement;
    }
    elementSuggestions.forEach((suggestion) => {
      const matchingSource = dataSources.find((source) => {
        if (source.type === suggestion.type)
          return true;
        if (suggestion.dataType === "TEMPLATE") {
          return Datasources.getDataTypeFromSourceType(source.type) === "TEMPLATE";
        }
        return false;
      });
      if (matchingSource) {
        updatedElement = applyDataPointToElement(
          updatedElement,
          matchingSource,
          suggestion.dataPointName
        );
      }
    });
    const parsed = parseElementName(element.name);
    if (isImageElement(updatedElement) && parsed.config?.customAssetConfig) {
      if (updatedElement.sources.length > 0) {
        let source = updatedElement.sources[0];
        const matchedDs = dataSources.find((ds) => ds.id === source.sourceId);
        if (matchedDs) {
          const dsConfig = Datasources.dataSourcesConfigs.find((c) => c.type === matchedDs.type);
          const correctPoint = dsConfig?.points.find((p) => {
            if (!p.autoImportName)
              return false;
            const names = Array.isArray(p.autoImportName) ? p.autoImportName : [p.autoImportName];
            return names.includes(parsed.name);
          });
          if (correctPoint && correctPoint.name !== source.name) {
            source = {
              sourceId: source.sourceId,
              name: correctPoint.name
            };
            updatedElement.sources = [source];
          }
        }
        updatedElement.data.initialContent = updatedElement.data.content;
        updatedElement.data.content = buildCustomAssetContentUrl(
          parsed.config.customAssetConfig,
          source.name,
          source.sourceId
        );
      }
      updatedElement.name = parsed.name;
    }
    return updatedElement;
  });
}
function applyDataPointToElement(element, dataSource, dataPointName) {
  let updatedEl = element;
  const magicString = `{${dataSource.id}:${dataPointName}}`;
  if (isTextElement2(updatedEl)) {
    updatedEl.data.content = magicString;
  }
  if (isImageElement(updatedEl)) {
    const teamAssetSource = mapDataPointToTeamAssetSource(dataPointName);
    if (teamAssetSource) {
      updatedEl = {
        ...updatedEl,
        type: "TEAM_ASSET",
        data: {
          ...updatedEl.data,
          content: "",
          source: teamAssetSource
        }
      };
    }
    const casinoAssetSource = mapDataPointToCasinoAssetSource(dataPointName);
    if (casinoAssetSource) {
      updatedEl = {
        ...updatedEl,
        position: {
          ...updatedEl.position,
          z: -1
        },
        type: "CASINO_ASSET",
        data: {
          ...updatedEl.data,
          content: "",
          source: casinoAssetSource
        }
      };
    }
  }
  if (!updatedEl.sources) {
    updatedEl.sources = [];
  }
  if (Datasources.AllEventDataPointNameSchema.safeParse(dataPointName).success && !updatedEl.sources.some(
    (source) => source.sourceId === dataSource.id && source.name === dataPointName
  )) {
    updatedEl.sources.push({
      sourceId: dataSource.id,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      name: dataPointName
    });
  }
  return updatedEl;
}
function mapDataPointToTeamAssetSource(dataPointName) {
  switch (dataPointName) {
    case "HOME_JERSEY":
    case "HOME_BADGE":
      return "HOME_ASSET";
    case "AWAY_JERSEY":
    case "AWAY_BADGE":
      return "AWAY_ASSET";
    case "COMPETITOR_JERSEY":
    case "COMPETITOR_BADGE":
      return "COMPETITOR_ASSET";
    case "OPPONENT_JERSEY":
    case "OPPONENT_BADGE":
      return "OPPONENT_ASSET";
    default:
      return void 0;
  }
}
function mapDataPointToCasinoAssetSource(dataPointName) {
  switch (dataPointName) {
    case "LANDSCAPE_IMAGE":
      return "LANDSCAPE_IMAGE";
    case "SQUARE_IMAGE":
      return "SQUARE_IMAGE";
    case "PORTRAIT_IMAGE":
      return "PORTRAIT_IMAGE";
    default:
      return void 0;
  }
}
var elementMappingUtils = {
  mapConceptDataSourceToAdTemplateDataSources,
  mapElements
};

// src/matchStatus.ts
var IN_PLAY_STATUSES = /* @__PURE__ */ new Set(["1st_half", "2nd_half", "1st_extra", "2nd_extra"]);
var PENALTY_STATUSES = /* @__PURE__ */ new Set(["penalties", "ap"]);
var hasPenaltyScore = (value) => value !== void 0 && value !== "";
function getMatchStatusShortLabel(status, locale, separator, homePenaltiesScore, awayPenaltiesScore) {
  const fullLocale = locale.toLowerCase().replace("_", "-");
  const baseLocale = fullLocale.split("-")[0];
  const label = SHORT_LABELS[fullLocale]?.[status] ?? SHORT_LABELS[baseLocale]?.[status] ?? SHORT_LABELS.en[status] ?? status;
  if (PENALTY_STATUSES.has(status) && hasPenaltyScore(homePenaltiesScore) && hasPenaltyScore(awayPenaltiesScore)) {
    return label + " " + homePenaltiesScore + separator + awayPenaltiesScore;
  }
  return label;
}
function resolveMatchStatusDisplay(status, clock, locale, penaltiesFormat, homePenaltiesScore, awayPenaltiesScore) {
  if (!status)
    return "";
  if (IN_PLAY_STATUSES.has(status) && clock)
    return clock;
  return getMatchStatusShortLabel(
    status,
    locale,
    penaltiesFormat.separator ?? ":",
    homePenaltiesScore,
    awayPenaltiesScore
  );
}
var SHORT_LABELS = {
  en: {
    not_started: "Scheduled",
    started: "Kick-off",
    "1st_half": "1st Half",
    "2nd_half": "2nd Half",
    overtime: "OT",
    "1st_extra": "ET 1",
    "2nd_extra": "ET 2",
    awaiting_penalties: "Await Pens",
    penalties: "Pens",
    awaiting_extra_time: "Await ET",
    interrupted: "Interrupted",
    abandoned: "Abandoned",
    postponed: "Postponed",
    start_delayed: "Delayed",
    cancelled: "Cancelled",
    halftime: "HT",
    extra_time_halftime: "ET HT",
    ended: "FT",
    aet: "FT (AET)",
    ap: "FT (Pens)"
  },
  de: {
    not_started: "Geplant",
    started: "Anpfiff",
    "1st_half": "1. HZ",
    "2nd_half": "2. HZ",
    overtime: "Verl.",
    "1st_extra": "Verl. 1",
    "2nd_extra": "Verl. 2",
    awaiting_penalties: "Warte Elf.",
    penalties: "Elf.",
    awaiting_extra_time: "Warte Verl.",
    interrupted: "Unterbr.",
    abandoned: "Abgeb.",
    postponed: "Versch.",
    start_delayed: "Verz\xF6g.",
    cancelled: "Abges.",
    halftime: "HZ",
    extra_time_halftime: "HZ Verl.",
    ended: "Ende",
    aet: "Ende (n. Verl.)",
    ap: "Ende (n. Elf.)"
  },
  es: {
    not_started: "Programado",
    started: "En juego",
    "1st_half": "1\xAA Parte",
    "2nd_half": "2\xAA Parte",
    overtime: "Pr\xF3rroga",
    "1st_extra": "Pr\xF3r. 1",
    "2nd_extra": "Pr\xF3r. 2",
    awaiting_penalties: "Espera pen.",
    penalties: "Penales",
    awaiting_extra_time: "Espera pr\xF3r.",
    interrupted: "Interr.",
    abandoned: "Suspend.",
    postponed: "Aplaz.",
    start_delayed: "Retras.",
    cancelled: "Cancel.",
    halftime: "Descanso",
    extra_time_halftime: "Desc. pr\xF3r.",
    ended: "Final",
    aet: "Final (Pr\xF3r.)",
    ap: "Final (Pen.)"
  },
  pt: {
    not_started: "Agendado",
    started: "Em jogo",
    "1st_half": "1\xAA Parte",
    "2nd_half": "2\xAA Parte",
    overtime: "Prol.",
    "1st_extra": "Prol. 1",
    "2nd_extra": "Prol. 2",
    awaiting_penalties: "Aguarda pen.",
    penalties: "Pen\xE1ltis",
    awaiting_extra_time: "Aguarda prol.",
    interrupted: "Interr.",
    abandoned: "Aband.",
    postponed: "Adiado",
    start_delayed: "Atras.",
    cancelled: "Cancel.",
    halftime: "Int.",
    extra_time_halftime: "Int. prol.",
    ended: "Final",
    aet: "Final (Prol.)",
    ap: "Final (Pen.)"
  },
  "pt-br": {
    not_started: "Agendado",
    started: "Em jogo",
    "1st_half": "1\xBA Tempo",
    "2nd_half": "2\xBA Tempo",
    overtime: "Prorr.",
    "1st_extra": "Prorr. 1",
    "2nd_extra": "Prorr. 2",
    awaiting_penalties: "Aguarda p\xEAn.",
    penalties: "P\xEAnaltis",
    awaiting_extra_time: "Aguarda prorr.",
    interrupted: "Interr.",
    abandoned: "Aband.",
    postponed: "Adiado",
    start_delayed: "Atras.",
    cancelled: "Cancel.",
    halftime: "Intervalo",
    extra_time_halftime: "Int. prorr.",
    ended: "Encerr.",
    aet: "Encerr. (Prorr.)",
    ap: "Encerr. (P\xEAn.)"
  },
  el: {
    not_started: "\u03A0\u03C1\u03BF\u03B3\u03C1.",
    started: "\u03A3\u03B5 \u03B5\u03BE\u03AD\u03BB\u03B9\u03BE\u03B7",
    "1st_half": "1\u03BF \u0397\u03BC.",
    "2nd_half": "2\u03BF \u0397\u03BC.",
    overtime: "\u03A0\u03B1\u03C1\u03AC\u03C4.",
    "1st_extra": "\u03A0\u03B1\u03C1\u03AC\u03C4. 1",
    "2nd_extra": "\u03A0\u03B1\u03C1\u03AC\u03C4. 2",
    awaiting_penalties: "\u0391\u03BD\u03B1\u03BC. \u03C0\u03AD\u03BD\u03B1\u03BB\u03C4\u03B9",
    penalties: "\u03A0\u03AD\u03BD\u03B1\u03BB\u03C4\u03B9",
    awaiting_extra_time: "\u0391\u03BD\u03B1\u03BC. \u03C0\u03B1\u03C1\u03AC\u03C4.",
    interrupted: "\u0394\u03B9\u03B1\u03BA\u03BF\u03C0\u03AE",
    abandoned: "\u0395\u03B3\u03BA\u03B1\u03C4.",
    postponed: "\u0391\u03BD\u03B1\u03B2\u03BB.",
    start_delayed: "\u039A\u03B1\u03B8\u03C5\u03C3\u03C4.",
    cancelled: "\u0391\u03BA\u03C5\u03C1.",
    halftime: "\u0397\u03BC\u03AF\u03C7.",
    extra_time_halftime: "\u0397\u03BC\u03AF\u03C7. \u03C0\u03B1\u03C1\u03AC\u03C4.",
    ended: "\u039B\u03AE\u03BE\u03B7",
    aet: "\u039B\u03AE\u03BE\u03B7 (\u03A0\u03B1\u03C1\u03AC\u03C4.)",
    ap: "\u039B\u03AE\u03BE\u03B7 (\u03A0\u03AD\u03BD\u03B1\u03BB\u03C4\u03B9)"
  },
  fr: {
    not_started: "Programm\xE9",
    started: "En cours",
    "1st_half": "1re MT",
    "2nd_half": "2e MT",
    overtime: "Prol.",
    "1st_extra": "Prol. 1",
    "2nd_extra": "Prol. 2",
    awaiting_penalties: "Att. TAB",
    penalties: "TAB",
    awaiting_extra_time: "Att. prol.",
    interrupted: "Interr.",
    abandoned: "Aband.",
    postponed: "Report\xE9",
    start_delayed: "Retard\xE9",
    cancelled: "Annul\xE9",
    halftime: "MT",
    extra_time_halftime: "MT prol.",
    ended: "Termin\xE9",
    aet: "Termin\xE9 (AP)",
    ap: "Termin\xE9 (TAB)"
  },
  da: {
    not_started: "Planlagt",
    started: "I gang",
    "1st_half": "1. halvleg",
    "2nd_half": "2. halvleg",
    overtime: "Forl.",
    "1st_extra": "Forl. 1",
    "2nd_extra": "Forl. 2",
    awaiting_penalties: "Afv. straffe",
    penalties: "Straffe",
    awaiting_extra_time: "Afv. forl.",
    interrupted: "Afbryd.",
    abandoned: "Opgivet",
    postponed: "Udsat",
    start_delayed: "Forsink.",
    cancelled: "Aflyst",
    halftime: "Pause",
    extra_time_halftime: "Pause (forl.)",
    ended: "Slut",
    aet: "Slut (forl.)",
    ap: "Slut (straffe)"
  },
  sv: {
    not_started: "Planerad",
    started: "P\xE5g\xE5r",
    "1st_half": "1:a",
    "2nd_half": "2:a",
    overtime: "F\xF6rl.",
    "1st_extra": "F\xF6rl. 1",
    "2nd_extra": "F\xF6rl. 2",
    awaiting_penalties: "Inv. straff",
    penalties: "Straff",
    awaiting_extra_time: "Inv. f\xF6rl.",
    interrupted: "Avbrott",
    abandoned: "Avbruten",
    postponed: "Uppskj.",
    start_delayed: "F\xF6rsen.",
    cancelled: "Inst\xE4lld",
    halftime: "Halvtid",
    extra_time_halftime: "HT (f\xF6rl.)",
    ended: "Slut",
    aet: "Slut (ET)",
    ap: "Slut (straff)"
  },
  fi: {
    not_started: "Suunnit.",
    started: "K\xE4ynniss\xE4",
    "1st_half": "1. jakso",
    "2nd_half": "2. jakso",
    overtime: "JA",
    "1st_extra": "JA 1",
    "2nd_extra": "JA 2",
    awaiting_penalties: "Odot. RP",
    penalties: "RP",
    awaiting_extra_time: "Odot. JA",
    interrupted: "Keskeyt.",
    abandoned: "Luov.",
    postponed: "Siirretty",
    start_delayed: "Viiv.",
    cancelled: "Peruttu",
    halftime: "Puoliaika",
    extra_time_halftime: "JA tauko",
    ended: "P\xE4\xE4tt.",
    aet: "P\xE4\xE4tt. (JA)",
    ap: "P\xE4\xE4tt. (RP)"
  },
  sw: {
    not_started: "Imepangwa",
    started: "Inaendelea",
    "1st_half": "Nusu 1",
    "2nd_half": "Nusu 2",
    overtime: "Ziada",
    "1st_extra": "Ziada 1",
    "2nd_extra": "Ziada 2",
    awaiting_penalties: "Subiri penalti",
    penalties: "Penalti",
    awaiting_extra_time: "Subiri ziada",
    interrupted: "Imesitishwa",
    abandoned: "Imeachwa",
    postponed: "Imeahirishwa",
    start_delayed: "Imechelewa",
    cancelled: "Imefutwa",
    halftime: "Mapumziko",
    extra_time_halftime: "Map. ziada",
    ended: "Imeisha",
    aet: "Imeisha (Ziada)",
    ap: "Imeisha (Pen.)"
  },
  bg: {
    not_started: "\u041F\u043B\u0430\u043D\u0438\u0440\u0430\u043D",
    started: "\u0417\u0430\u043F\u043E\u0447\u043D\u0430",
    "1st_half": "1-\u0432\u043E",
    "2nd_half": "2-\u0440\u043E",
    overtime: "\u041F\u0440\u043E\u0434.",
    "1st_extra": "\u041F\u0440\u043E\u0434. 1",
    "2nd_extra": "\u041F\u0440\u043E\u0434. 2",
    awaiting_penalties: "\u041E\u0447\u0430\u043A\u0432. \u0434\u0443\u0437\u043F\u0438",
    penalties: "\u0414\u0443\u0437\u043F\u0438",
    awaiting_extra_time: "\u041E\u0447\u0430\u043A\u0432. \u043F\u0440\u043E\u0434.",
    interrupted: "\u041F\u0440\u0435\u043A\u044A\u0441\u043D.",
    abandoned: "\u041F\u0440\u0435\u043A\u0440\u0430\u0442.",
    postponed: "\u041E\u0442\u043B\u043E\u0436.",
    start_delayed: "\u0417\u0430\u0431\u0430\u0432.",
    cancelled: "\u041E\u0442\u043C\u0435\u043D.",
    halftime: "\u041F\u043E\u0447\u0438\u0432\u043A\u0430",
    extra_time_halftime: "\u041F\u043E\u0447. \u043F\u0440\u043E\u0434.",
    ended: "\u041A\u0440\u0430\u0439",
    aet: "\u041A\u0440\u0430\u0439 (\u041F\u0440\u043E\u0434.)",
    ap: "\u041A\u0440\u0430\u0439 (\u0414\u0443\u0437\u043F\u0438)"
  },
  it: {
    not_started: "Programm.",
    started: "In corso",
    "1st_half": "1\xB0 T.",
    "2nd_half": "2\xB0 T.",
    overtime: "Suppl.",
    "1st_extra": "Suppl. 1",
    "2nd_extra": "Suppl. 2",
    awaiting_penalties: "Att. rigori",
    penalties: "Rigori",
    awaiting_extra_time: "Att. suppl.",
    interrupted: "Interr.",
    abandoned: "Abband.",
    postponed: "Rinviata",
    start_delayed: "Ritardo",
    cancelled: "Annull.",
    halftime: "Interv.",
    extra_time_halftime: "Interv. suppl.",
    ended: "Finale",
    aet: "Finale (TS)",
    ap: "Finale (Rig.)"
  },
  et: {
    not_started: "Planeer.",
    started: "K\xE4imas",
    "1st_half": "1. poolaeg",
    "2nd_half": "2. poolaeg",
    overtime: "Lisaaeg",
    "1st_extra": "LA 1",
    "2nd_extra": "LA 2",
    awaiting_penalties: "Ootel pen.",
    penalties: "Pen.",
    awaiting_extra_time: "Ootel LA",
    interrupted: "Katk.",
    abandoned: "Loobutud",
    postponed: "Edasi l\xFCk.",
    start_delayed: "Viiv.",
    cancelled: "T\xFChist.",
    halftime: "Poolaeg",
    extra_time_halftime: "LA vaheaeg",
    ended: "L\xF5pp",
    aet: "L\xF5pp (LA)",
    ap: "L\xF5pp (Pen.)"
  },
  nl: {
    not_started: "Gepland",
    started: "Bezig",
    "1st_half": "1e",
    "2nd_half": "2e",
    overtime: "Verl.",
    "1st_extra": "Verl. 1",
    "2nd_extra": "Verl. 2",
    awaiting_penalties: "Wacht pen.",
    penalties: "Pen.",
    awaiting_extra_time: "Wacht verl.",
    interrupted: "Onderbr.",
    abandoned: "Gestaakt",
    postponed: "Uitgest.",
    start_delayed: "Vertr.",
    cancelled: "Afgelast",
    halftime: "Rust",
    extra_time_halftime: "Rust (verl.)",
    ended: "Afgelopen",
    aet: "Afgel. (verl.)",
    ap: "Afgel. (pen.)"
  },
  ru: {
    not_started: "\u0417\u0430\u043F\u043B\u0430\u043D.",
    started: "\u0418\u0434\u0435\u0442",
    "1st_half": "1-\u0439 \u0442\u0430\u0439\u043C",
    "2nd_half": "2-\u0439 \u0442\u0430\u0439\u043C",
    overtime: "\u0414\u0412",
    "1st_extra": "\u0414\u0412 1",
    "2nd_extra": "\u0414\u0412 2",
    awaiting_penalties: "\u0416\u0434\u0451\u0442 \u043F\u0435\u043D.",
    penalties: "\u041F\u0435\u043D.",
    awaiting_extra_time: "\u0416\u0434\u0451\u0442 \u0414\u0412",
    interrupted: "\u041F\u0440\u0435\u0440\u0432\u0430\u043D",
    abandoned: "\u041F\u0440\u0435\u043A\u0440\u0430\u0449\u0451\u043D",
    postponed: "\u041F\u0435\u0440\u0435\u043D\u0435\u0441.",
    start_delayed: "\u0417\u0430\u0434\u0435\u0440\u0436.",
    cancelled: "\u041E\u0442\u043C\u0435\u043D\u0451\u043D",
    halftime: "\u041F\u0435\u0440\u0435\u0440\u044B\u0432",
    extra_time_halftime: "\u041F\u0435\u0440\u0435\u0440\u044B\u0432 \u0414\u0412",
    ended: "\u041E\u043A\u043E\u043D\u0447.",
    aet: "\u041E\u043A\u043E\u043D\u0447. (\u0414\u0412)",
    ap: "\u041E\u043A\u043E\u043D\u0447. (\u043F\u0435\u043D.)"
  },
  ro: {
    not_started: "Programat",
    started: "\xCEn joc",
    "1st_half": "Repr. 1",
    "2nd_half": "Repr. 2",
    overtime: "Prel.",
    "1st_extra": "Prel. 1",
    "2nd_extra": "Prel. 2",
    awaiting_penalties: "A\u0219teapt\u0103 pen.",
    penalties: "Penalty",
    awaiting_extra_time: "A\u0219teapt\u0103 prel.",
    interrupted: "\xCEntrer.",
    abandoned: "Aband.",
    postponed: "Am\xE2nat",
    start_delayed: "\xCEnt\xE2rz.",
    cancelled: "Anulat",
    halftime: "Pauz\u0103",
    extra_time_halftime: "Pauz\u0103 prel.",
    ended: "Final",
    aet: "Final (Prel.)",
    ap: "Final (Pen.)"
  },
  cs: {
    not_started: "Pl\xE1n.",
    started: "V b\u011Bhu",
    "1st_half": "1. polo\u010Das",
    "2nd_half": "2. polo\u010Das",
    overtime: "Prodl.",
    "1st_extra": "Prodl. 1",
    "2nd_extra": "Prodl. 2",
    awaiting_penalties: "\u010Cek\xE1 pen.",
    penalties: "Penalty",
    awaiting_extra_time: "\u010Cek\xE1 prodl.",
    interrupted: "P\u0159eru\u0161.",
    abandoned: "Ukon\u010D.",
    postponed: "Odlo\u017E.",
    start_delayed: "Zpo\u017Ed.",
    cancelled: "Zru\u0161.",
    halftime: "P\u0159est.",
    extra_time_halftime: "P\u0159est. prodl.",
    ended: "Konec",
    aet: "Konec (PP)",
    ap: "Konec (Pen.)"
  },
  sk: {
    not_started: "Pl\xE1n.",
    started: "V hre",
    "1st_half": "1. pol\u010Das",
    "2nd_half": "2. pol\u010Das",
    overtime: "Predl.",
    "1st_extra": "Predl. 1",
    "2nd_extra": "Predl. 2",
    awaiting_penalties: "\u010Cak\xE1 pen.",
    penalties: "Penalty",
    awaiting_extra_time: "\u010Cak\xE1 predl.",
    interrupted: "Preru\u0161.",
    abandoned: "Ukon\u010D.",
    postponed: "Odlo\u017E.",
    start_delayed: "Onesk.",
    cancelled: "Zru\u0161.",
    halftime: "Prest.",
    extra_time_halftime: "Prest. predl.",
    ended: "Koniec",
    aet: "Koniec (Predl.)",
    ap: "Koniec (Pen.)"
  },
  pl: {
    not_started: "Zaplan.",
    started: "W grze",
    "1st_half": "1. po\u0142.",
    "2nd_half": "2. po\u0142.",
    overtime: "Dogr.",
    "1st_extra": "Dogr. 1",
    "2nd_extra": "Dogr. 2",
    awaiting_penalties: "Czeka na karne",
    penalties: "Karne",
    awaiting_extra_time: "Czeka na dogr.",
    interrupted: "Przerw.",
    abandoned: "Przerwany",
    postponed: "Prze\u0142.",
    start_delayed: "Op\xF3\u017An.",
    cancelled: "Odwo\u0142.",
    halftime: "Przerwa",
    extra_time_halftime: "Prz. dogr.",
    ended: "Koniec",
    aet: "Koniec (Dogr.)",
    ap: "Koniec (Karne)"
  },
  ja: {
    not_started: "\u4E88\u5B9A",
    started: "\u9032\u884C\u4E2D",
    "1st_half": "\u524D\u534A",
    "2nd_half": "\u5F8C\u534A",
    overtime: "\u5EF6\u9577",
    "1st_extra": "\u5EF6\u9577 \u524D",
    "2nd_extra": "\u5EF6\u9577 \u5F8C",
    awaiting_penalties: "PK\u5F85\u3061",
    penalties: "PK",
    awaiting_extra_time: "\u5EF6\u9577\u5F85\u3061",
    interrupted: "\u4E2D\u65AD",
    abandoned: "\u4E2D\u6B62",
    postponed: "\u5EF6\u671F",
    start_delayed: "\u9045\u5EF6",
    cancelled: "\u4E2D\u6B62",
    halftime: "HT",
    extra_time_halftime: "\u5EF6\u9577HT",
    ended: "\u7D42\u4E86",
    aet: "\u5EF6\u9577\u7D42\u4E86",
    ap: "PK\u7D42\u4E86"
  },
  lt: {
    not_started: "Planuota",
    started: "Vyksta",
    "1st_half": "1 k\u0117l.",
    "2nd_half": "2 k\u0117l.",
    overtime: "Prat.",
    "1st_extra": "Prat. 1",
    "2nd_extra": "Prat. 2",
    awaiting_penalties: "Lauk. baud.",
    penalties: "Baudiniai",
    awaiting_extra_time: "Lauk. prat.",
    interrupted: "Pertraukta",
    abandoned: "Nutraukta",
    postponed: "Atid\u0117ta",
    start_delayed: "V\u0117luoja",
    cancelled: "At\u0161aukta",
    halftime: "Pertrauka",
    extra_time_halftime: "Prat. pertr.",
    ended: "Baigta",
    aet: "Baigta (Prat.)",
    ap: "Baigta (Baud.)"
  },
  lv: {
    not_started: "Pl\u0101nots",
    started: "Notiek",
    "1st_half": "1. pusl.",
    "2nd_half": "2. pusl.",
    overtime: "Pap. laiks",
    "1st_extra": "Pap. 1",
    "2nd_extra": "Pap. 2",
    awaiting_penalties: "Gaida pend.",
    penalties: "Pend.",
    awaiting_extra_time: "Gaida pap.",
    interrupted: "P\u0101rtraukts",
    abandoned: "P\u0101rtraukts (gal.)",
    postponed: "P\u0101rcelts",
    start_delayed: "Aizkav.",
    cancelled: "Atcelts",
    halftime: "P\u0101rtr.",
    extra_time_halftime: "Pap. p\u0101rtr.",
    ended: "Beidzies",
    aet: "Beidzies (Pap.)",
    ap: "Beidzies (Pend.)"
  },
  hr: {
    not_started: "Zakaz.",
    started: "U tijeku",
    "1st_half": "1. pol.",
    "2nd_half": "2. pol.",
    overtime: "Prod.",
    "1st_extra": "Prod. 1",
    "2nd_extra": "Prod. 2",
    awaiting_penalties: "\u010Ceka pen.",
    penalties: "Penali",
    awaiting_extra_time: "\u010Ceka prod.",
    interrupted: "Prekid",
    abandoned: "Napu\u0161tena",
    postponed: "Odgo\u0111.",
    start_delayed: "Kasni",
    cancelled: "Otkaz.",
    halftime: "Poluvr.",
    extra_time_halftime: "Poluvr. prod.",
    ended: "Zavr\u0161.",
    aet: "Zavr\u0161. (Prod.)",
    ap: "Zavr\u0161. (Pen.)"
  },
  sr: {
    not_started: "Zakaz.",
    started: "U toku",
    "1st_half": "1. pol.",
    "2nd_half": "2. pol.",
    overtime: "Prod.",
    "1st_extra": "Prod. 1",
    "2nd_extra": "Prod. 2",
    awaiting_penalties: "\u010Ceka pen.",
    penalties: "Penali",
    awaiting_extra_time: "\u010Ceka prod.",
    interrupted: "Prekid",
    abandoned: "Napu\u0161tena",
    postponed: "Odlo\u017E.",
    start_delayed: "Kasni",
    cancelled: "Otkaz.",
    halftime: "Poluvr.",
    extra_time_halftime: "Poluvr. prod.",
    ended: "Kraj",
    aet: "Kraj (Prod.)",
    ap: "Kraj (Pen.)"
  }
};
export {
  CognitoAuth,
  FeatureFlagContext,
  FeatureFlagProvider,
  TimeFormat,
  UserContext_default as UserContext,
  applyFontVariantAdjustments,
  createGenericContext,
  currencies,
  elementMappingUtils,
  findBestMatchingCustomFont,
  findClosestFontVariant,
  fmtCountdownV2,
  fmtDate,
  fmtDay,
  fmtMonth,
  fmtReference,
  fmtTextDate,
  fmtTime,
  formatCurrency,
  formatFontMismatchMessage,
  formatOdds,
  getMatchStatusShortLabel,
  getWeightLabel,
  indexObjArray,
  notEmpty,
  parseFontStretch,
  parseFontStyle,
  parseFontWeight,
  parseFonts,
  resolveMatchStatusDisplay,
  svgUtils,
  toSlug,
  uniqArray,
  useEffectOnce,
  useFeatureFlags,
  useInterval,
  useIsomorphicLayoutEffect,
  useObjectState,
  usePrevious
};
